import type {AxiosError} from 'axios';
import {HttpStatusCode} from 'axios';
import type {UUID} from 'crypto';
import type {TFunction} from 'i18next';
import type {i18n} from 'i18next';
import {type ReadonlyURLSearchParams} from 'next/navigation';

import {ChipVariant} from '@/components/base/Chip';
import type {TelephoneOptions} from '@/components/base/PhoneNumber';
import type {SelectOption} from '@/components/base/Select';
import {RecordStatus} from '@/enums';
import {MessageRecordModuleType} from '@/enums/chatRecords';
import {ContactRealism, ContactType, LegalNameType, ShareStatus} from '@/enums/contact';
import {DomainBanStatus} from '@/enums/domain';
import {FileSizeUnits, FileUploadStatus} from '@/enums/files';
import {GlidStatus} from '@/enums/glid';
import type {ApiResponse} from '@/models/ApiResponse';
import type {AxiosResponse} from '@/models/AxiosResponse';
import type {ContactDetails, IBusinessCard, IContactLite, ILegalName, SharedInContact, ValueWithPronounce} from '@/models/Contacts';
import type {ICountryFromIP, ITimeZone} from '@/models/Countries';
import type ICountry from '@/models/Countries';
import type ICountries from '@/models/Countries';
import type {Domain} from '@/models/Domain';
import type ErrorResponse from '@/models/ErrorResponse';
import type IMFAAuthStatus from '@/models/IMFAAuthStatus';
import {IPType} from '@/models/IPCaptcha';
import type ILanguage from '@/models/Languages';
import type INotification from '@/models/Notifications';
import {NotificationType} from '@/models/Notifications';
import type {AuthDetails, ISession} from '@/models/Session';
import {SessionState} from '@/models/Session';
import type {LandingPreference} from '@/models/User';
import axios from '@/utils/helpers/axios';

import {DEFAULT_PAGE, DEFAULT_PAGE_SIZE_FOR_CARDS, DEFAULT_PAGE_SIZE_FOR_INFINITE_SCROLL, PERMANENT_DELETION_DAYS} from '../constants';
import {COUNTRY_URLS, DOMAIN_CONTROL} from '../constants/api-urls';
import {CountrySupportedModules, EntityType, LandingPagePreferenceTypes, SupportedStatus, SupportedTypes} from '../enums';
import {ConnectionStatus} from '../enums/connections';
import {AuthStatus} from '../enums/Session';

export function parseJwt(token: string) {
  if (!token) {
    return;
  }
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace('-', '+').replace('_', '/');
  return JSON.parse(globalThis.atob(base64));
}
export const generateLanguageOptions = (options: ILanguage[]): SelectOption[] => {
  return options.map((option) => ({value: option.iso639['1'], label: option.name}));
};

export function getTimeZoneFromCountry(countries: ICountry[], countryCode: string, timeZoneCode: string) {
  let zone: ITimeZone | undefined;
  for (let i = 0; i < (countries || [])?.length; i++) {
    if (countries![i].iso3166_1.alpha_2 === countryCode) {
      const timeZone = countries![i].timeZone.find((x) => x.shortName === timeZoneCode);
      if (timeZone) {
        zone = timeZone;
        break;
      }
    }
  }
  return zone;
}

export function isValidIPAddress(type: string, address: string) {
  const isValidHexaDecimalChar = (char: string) => {
    const a = parseInt(char, 16);
    return a.toString(16) === char;
  };

  if (type === IPType.IPV4) {
    const octets = address.split('.');
    if (octets.length === 4 && !octets.some((octet) => octet === '')) {
      return !octets.some((octet) => isNaN(Number(octet)) || parseInt(octet) < 0 || parseInt(octet) > 255);
    }
    return false;
  } else if (type === IPType.IPV6) {
    if (address === '::') return true;
    const segments = address.trim().split(':');
    const count = segments.length;

    if (count > 8) {
      return false;
    }
    if (segments[count - 1].split('.').length > 1) {
      if (isValidIPAddress(IPType.IPV4, segments[count - 1])) {
        segments.splice(count - 1, 1, '0');
      } else return false;
    }
    const compressedIndex = segments.indexOf('');
    const lastCompressedIndex = segments.lastIndexOf('');
    if (compressedIndex !== -1) {
      if (
        (compressedIndex === 0 && lastCompressedIndex === 1) ||
        (compressedIndex === count - 2 && lastCompressedIndex === count - 1) ||
        (compressedIndex === lastCompressedIndex && compressedIndex != count - 1)
      ) {
        let replaceIndex = 1;
        if (compressedIndex === 0 || (compressedIndex === count - 2 && lastCompressedIndex === count - 1)) {
          replaceIndex = 2;
        }

        segments.splice(compressedIndex, replaceIndex, ...Array(9 - segments.length + replaceIndex - 1).fill('0'));
      } else return false;
    } else if (segments.length !== 8) {
      return false;
    }
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      if (segment.length < 0 || segment.length > 4 || segment.split('').some((char) => !isValidHexaDecimalChar(char.toLowerCase())))
        return false;
    }
    return true;
  }
}

export function validateSession(session: ISession, auth: AuthDetails) {
  if (
    [AuthStatus.EXPIRED_WITH_FAILED, AuthStatus.LOG_OUT, AuthStatus.EXPIRED, AuthStatus.CLOSE_SESSION].includes(auth.status) ||
    session.sessionState === SessionState.Inactive
  ) {
    return false;
  }

  return true;
}

export function getTelephones(countries: ICountries[]) {
  return countries.reduce((acc: TelephoneOptions[], v) => {
    if (v.telephone.dialCode)
      acc.push({
        countryCode: v.iso3166_1.alpha_2,
        countryName: v.countryPreferredName,
        dialCode: v.telephone.dialCode,
        patterns: v.telephone.stringFormats,
        maxPhoneLength: v.telephone.stringFormats.length > 0 ? v.telephone.stringFormats[0].split('#').length - 1 : 10,
      });
    return acc;
  }, []);
}
export function compareSort(value: Array<any>, field: string) {
  return value.sort((a, b) => {
    if (a[field] > b[field]) {
      return 1;
    } else if (a[field] < b[field]) {
      return -1;
    } else {
      return 0;
    }
  });
}

export const getTelephoneFormat = (countries: ICountry[], code: string, country: string) => {
  return countries.filter((v) => v.telephone.dialCode === code.replaceAll('+', '') && v.iso3166_1.alpha_2 === country)?.[0];
};
export const getPhoneSupportStatus = (countries: ICountry[], phoneCode: string, countryCode: string) => {
  if (phoneCode && countries) {
    const country: ICountry = countries?.filter(
      (country) => country.telephone.dialCode === phoneCode.replaceAll('+', '') && country.iso3166_1.alpha_2 === countryCode,
    )[0];
    const selected = country?.support?.find((support) => {
      if (support.type === CountrySupportedModules.USER_ACCOUNT_PHONE_NUMBER) return support.status;
    });
    return selected?.status;
  }
};
export const getSupportedTelephones = (countries: ICountry[]) => {
  return countries.reduce((options: TelephoneOptions[], country: ICountry) => {
    if (country.telephone.dialCode) {
      const filteredCountries = country.support.reduce((acc: TelephoneOptions[], support) => {
        if (support.type === CountrySupportedModules.USER_ACCOUNT_PHONE_NUMBER && support.status === SupportedStatus.SUPPORTED) {
          acc.push({
            countryName: country.countryPreferredName,
            countryCode: country.iso3166_1.alpha_2,
            dialCode: country.telephone.dialCode,
            patterns: country.telephone.stringFormats,
            maxPhoneLength: country.telephone.stringFormats.length > 0 ? country.telephone.stringFormats[0].split('#').length - 1 : 10,
          });
        }
        return acc;
      }, []);
      options.push(...filteredCountries);
    }
    return options;
  }, []);
};
export const getCountryFromIP = async () => {
  const res = await axios.get<AxiosResponse<ICountryFromIP>>(COUNTRY_URLS.IP_COUNTRY);
  return res.data.data;
};

export const verifyIPSupported = () => {
  return true;
};
export const verifyEmailIsBanned = async () => {
  return false;
};
export const padZero = (value: number) => {
  return value < 10 ? '0' + value : value;
};
export const formatTime = (time: string, includeSeconds: boolean) => {
  const timeSplit = time.split(':');
  if (includeSeconds) return [padZero(Number(timeSplit[0])), padZero(Number(timeSplit[1])), padZero(Number(timeSplit[2]))].join(':');
  return [padZero(Number(timeSplit[0])), padZero(Number(timeSplit[1]))].join(':');
};

export const formatDateTime = (
  timeZone: string = 'UTC',
  storedDateTime: string | Date,
  languageCode: string = 'en',
  hour12: number = 12,
  countries: ICountry[],
  separator: string = '',
  excludeSeconds: boolean = true,
  isMonthShort: boolean = true,
  dateSeparator: string = '-',
) => {
  if (timeZone && storedDateTime) {
    const timeZoneSplit = timeZone.split('#');
    let utc = '';
    let calculatedTimeZone = '';
    if (timeZoneSplit.length > 1) {
      if (timeZoneSplit[2]) {
        utc = timeZoneSplit[2];
        calculatedTimeZone = timeZoneSplit[1];
      } else {
        const country = countries.find((x) => x.iso3166_1.alpha_2 === timeZoneSplit[0]);
        const zone = country?.timeZone.find((x) => x.shortName === timeZoneSplit[1]);
        calculatedTimeZone = zone?.shortName || 'UTC';
        if (!isDaylightSavingTimeActive(zone?.region[0] || '')) {
          utc = country?.timeZone.find((x) => x.shortName === zone?.polCode1)?.utcCode || 'UTC';
        } else {
          utc = country?.timeZone.find((x) => x.shortName === zone?.polCode2)?.utcCode || 'UTC';
        }
      }
    } else {
      utc = timeZoneSplit[0];
    }
    const targetDateTime = getDateTimeFromTimeZoneUTC(utc, new Date(storedDateTime))
      .toLocaleString(languageCode, {hour12: hour12 === 12})
      .replace(',', '')
      .split(' ');
    const [month, day, year] = targetDateTime[0].split('/');
    const formattedDate = `${day}${dateSeparator}${new Date([month, day, year].join('/')).toLocaleString(languageCode, {
      month: isMonthShort ? 'short' : 'long',
    })}${dateSeparator}${year}`;
    return [
      formattedDate,
      separator,
      formatTime(targetDateTime[1], !excludeSeconds),
      targetDateTime[2]?.toUpperCase(),
      timeZoneSplit.length > 1 ? calculatedTimeZone.toUpperCase() : timeZoneSplit[0].toUpperCase(),
    ].join(' ');
  } else return '';
};

export const getFormattedDateAndTime = (
  timeZone: string = 'UTC',
  storedDateTime: string | Date,
  language: string = 'en',
  hour12: number = 12,
  countries: ICountry[],
) => {
  const formatted = formatDateTime(timeZone, storedDateTime, language, hour12, countries);
  const parts = formatted.split(' ');
  return {date: parts[0], time: parts.splice(2).join(' ')};
};

export function debounce<Params extends any[]>(func: (...args: Params) => any, timeout = 300) {
  let timer: NodeJS.Timeout;
  return (...args: Params) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, timeout);
  };
}

export function convertDateToUTC(date: Date) {
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000 + date.getTimezoneOffset() * 60000);
}

export const getModuleSupportedCountries = (countries: ICountry[], moduleName: CountrySupportedModules) => {
  return countries.filter((v) => v.support.some((s) => s.type === moduleName && s.status === SupportedStatus.SUPPORTED));
};
export const getSupportedLanguages = (languages: ILanguage[]) => {
  return languages.filter((v) => v.support.some((s) => s.type === SupportedTypes.USER_LOCALE && s.status === SupportedStatus.SUPPORTED));
};

export function isDaylightSavingTimeActive(countryCode: string) {
  const now = new Date();
  const dateTimeFormatOfNow = Intl.DateTimeFormat('en-US', {timeZone: countryCode, timeZoneName: 'short'});
  const dateTimeFormatOfJan = Intl.DateTimeFormat('en-US', {
    timeZone: countryCode,
    timeZoneName: 'short',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
  });

  const [{value: timeZoneOfNow}] = dateTimeFormatOfNow.formatToParts(now).filter((part) => part.type === 'timeZoneName');
  const [{value: timeZoneOfJan}] = dateTimeFormatOfJan
    .formatToParts(new Date(now.getFullYear(), 0, 1))
    .filter((part) => part.type === 'timeZoneName');

  return timeZoneOfNow != timeZoneOfJan;
}

export const getDateTimeFromTimeZoneUTC = (utc: string, date?: Date, utcOffsetMins?: number) => {
  const dt = date || new Date();
  let utcOffset = 0;

  if (utc) {
    if (utc.includes('+')) {
      const offset = utc.split('+');
      const hrMin = offset?.[1]?.split(':') || [];
      utcOffset = Number(hrMin[0]) * 60 + Number(hrMin[1] ? hrMin[1] : 0);
    } else if (utc.includes('-')) {
      const offset = utc.split('-');
      const hrMin = offset?.[1]?.split(':') || [];

      utcOffset = -1 * (Number(hrMin[0]) * 60 + Number(hrMin[1] ? hrMin[1] : 0));
    }
  } else {
    utcOffset = utcOffsetMins || -1 * dt.getTimezoneOffset();
  }

  const utcDate = new Date(dt.getTime() + dt.getTimezoneOffset() * 60000 + utcOffset * 60000);
  return utcDate;
};

export const get12HrTime = (hrs: number) => {
  return hrs > 12 ? hrs - 12 : hrs === 0 ? 12 : hrs;
};

export const getTimeMode = (hrs: number) => {
  return hrs > 11 ? 'PM' : 'AM';
};

export const getUpdatedSearchParamsString = (
  currentSearchParams?: ReadonlyURLSearchParams,
  values: Record<string, any> = {},
  deletedParams: string[] = [],
) => {
  const searchParams = new URLSearchParams(currentSearchParams.toString());
  deletedParams.forEach((param) => {
    searchParams.delete(param);
  });
  Object.entries(values).forEach(([key, val]) => {
    searchParams.set(key, val);
  });
  return searchParams.toString();
};

export const getUserAccountDeleteStatus = (deleteInitiatedAt: string) => {
  const deleteRecoveredAt = new Date(new Date(deleteInitiatedAt).setDate(new Date(deleteInitiatedAt).getDate() + PERMANENT_DELETION_DAYS));
  if (deleteRecoveredAt < new Date()) {
    return RecordStatus.DELETED;
  }
  return RecordStatus.PENDING_DELETION;
};
export const getDomainsStatus = (domains: string[]) => {
  return axios.post<ApiResponse<Domain[]>>(DOMAIN_CONTROL.GET_DOMAINS_STATUS, {domains});
};

export const getDomainFromEmail = (email: string) => email.split('@')[1]?.toLowerCase();

export const isDomainBanned = (domain: Domain) => !!(domain.banStatus === DomainBanStatus.Banned || domain.inheritedBannedBy);

export const isAllSpaces = (value: string) => value.trim().length === 0;

export const toAbsoluteURL = (url: string): string => {
  return new URL(url, window.location.href).href;
};

// Check if it is hash anchor or same page anchor
export const isHashAnchor = (currentUrl: string, newUrl: string): boolean => {
  const current = new URL(toAbsoluteURL(currentUrl));
  const next = new URL(toAbsoluteURL(newUrl));
  return current.href.split('#')[0] === next.href.split('#')[0];
};
export function isAnchorOfCurrentUrl(currentUrl: string, newUrl: string) {
  const currentUrlObj = new URL(currentUrl);
  const newUrlObj = new URL(newUrl);
  // Compare hostname, pathname, and search parameters
  if (
    currentUrlObj.hostname === newUrlObj.hostname &&
    currentUrlObj.pathname === newUrlObj.pathname &&
    currentUrlObj.search === newUrlObj.search
  ) {
    // Check if the new URL is just an anchor of the current URL page
    const currentHash = currentUrlObj.hash;
    const newHash = newUrlObj.hash;
    return currentHash !== newHash && currentUrlObj.href.replace(currentHash, '') === newUrlObj.href.replace(newHash, '');
  }
  return false;
}

export const getNotificationFromError = (error: AxiosError<ErrorResponse>): INotification => {
  const statusCode = error.response?.data.statusCode;
  const msg = error.response?.data.errorType || 'Sorry, Something happened!';
  const subMsg = error.response?.data.errorMessage || '500';

  switch (statusCode) {
    case HttpStatusCode.BadRequest:
      return {type: NotificationType.WARNING, msg, subMsg};
    default:
      return {type: NotificationType.WARNING, msg, subMsg};
  }
};

export function getChipVariantByStatus(status: string) {
  switch (status) {
    case GlidStatus.PENDING:
    case 'Invite':
    case 'Verified':
    case 'Selected':
      return ChipVariant.BLUE;
    case GlidStatus.RESERVED:
    case ConnectionStatus.ACTIVE:
    case 'Primary':
    case FileUploadStatus.COMPLETE:
    case FileUploadStatus.PRE_EXISTING:
      return ChipVariant.GREEN;
    case GlidStatus.UNRESERVED:
    case ConnectionStatus.IGNORED:
    case ConnectionStatus.INACTIVE:
    case 'Unverified':
      return ChipVariant.DEFAULT;
    case 'Pending Approval':
    case ConnectionStatus.INVITED:
    case FileUploadStatus.PENDING_REVIEW:
    case FileUploadStatus.PROCESSING:
      return ChipVariant.YELLOW;
    case FileUploadStatus.FAILED:
      return ChipVariant.RED;
  }
}

export const getMfaDisplayText = (
  {didUserOptMobile, didUserOptMail, isMobileVerified, isEmailVerified}: IMFAAuthStatus,
  t: TFunction<string, undefined>,
) => {
  let errMsg: string = '';
  if (didUserOptMail && didUserOptMobile) {
    if (!isMobileVerified && !isEmailVerified) {
      errMsg = t('securityCode.both');
    } else if (isEmailVerified && !isMobileVerified) {
      errMsg = t('securityCode.phone');
    } else {
      errMsg = t('securityCode.email');
    }
  } else if (didUserOptMail && !isEmailVerified) {
    errMsg = t('securityCode.email');
  } else {
    errMsg = t('securityCode.phone');
  }
  return errMsg;
};

export function getShareStatusCSS(shareStatus: ShareStatus) {
  switch (shareStatus) {
    case ShareStatus.NEW:
      return 'bg-green-bright';
    case ShareStatus.UPDATES:
      return 'bg-yellow-bright ';
    case ShareStatus.IGNORED:
    case ShareStatus.DIFFERENCES:
      return 'bg-base-500';
  }
}
export function getModuleIcon(moduleName: MessageRecordModuleType) {
  switch (moduleName) {
    case MessageRecordModuleType.Chat:
      return 'bubble';
    case MessageRecordModuleType.Contact:
      return 'contact';
    case MessageRecordModuleType.File:
      return 'file';
    case MessageRecordModuleType.Interview:
      return 'interviewee';
    case MessageRecordModuleType.Jobs:
      return 'job';
    case MessageRecordModuleType.WorkerProfile:
      return 'worker';
  }
}

export const getPersonalMobileArray = (contactDetails: ContactDetails) => {
  const mobile = [];
  if (contactDetails.personalCell) {
    mobile.push({...contactDetails.personalCell, classification: 'personalCell'});
  }
  if (contactDetails.homeFax) {
    mobile.push({...contactDetails.homeFax, classification: 'homeFax'});
  }
  if (contactDetails.homePager) {
    mobile.push({...contactDetails.homePager, classification: 'homePager'});
  }
  if (contactDetails.homePhone) {
    mobile.push({...contactDetails.homePhone, classification: 'homePhone'});
  }
  return mobile;
};

export const getBusinessMobileArray = (contactDetails: IBusinessCard) => {
  const mobile = [];
  if (contactDetails.deskPhone) {
    mobile.push({...contactDetails.deskPhone, classification: 'deskPhone'});
  }
  if (contactDetails.workMobile) {
    mobile.push({...contactDetails.workMobile, classification: 'workMobile'});
  }
  if (contactDetails.workFax) {
    mobile.push({...contactDetails.workFax, classification: 'workFax'});
  }
  if (contactDetails.workPager) {
    mobile.push({...contactDetails.workPager, classification: 'workPager'});
  }
  return mobile;
};
export function getHyphenFormattedNumber(number: string, hyphenAt: number) {
  const length = number.length;
  const numberOfHyphens = length / hyphenAt;
  let formattedString = '';
  for (let i = 0; i < numberOfHyphens; i++) {
    const start = i * hyphenAt;
    formattedString = `${formattedString}${number.slice(start, (i + 1) * hyphenAt)}-`;
  }
  return formattedString.substring(0, formattedString.length - 1);
}

export function getContactIconByType(type?: ContactRealism) {
  switch (type) {
    case ContactRealism.TEST:
      return 'experiment-flask';
    case ContactRealism.REAL:
      return '';
    case ContactRealism.GHOST:
      return 'ghost';
    case ContactRealism.GHOST_TEST:
      return 'ghost-test';
    case ContactRealism.MERGED:
      return 'merge';
    default:
      return '';
  }
}

export function getContactIconFromContact(contact?: IContactLite | ContactDetails): string {
  if (!contact) {
    return '';
  }
  let contactType = contact.realism;
  if (contact.isMerged) {
    contactType = ContactRealism.MERGED;
  }
  return getContactIconByType(contactType);
}
export const getContactDropdownTitle = (pathName: string, language: string = 'en', contactType: ContactType = undefined) => {
  const path = language === 'en' ? '/' + language + pathName : pathName;
  if (path.startsWith(`/${language}/company`)) {
    return 'Company Contacts';
  } else if (path.startsWith(`/${language}/user`)) {
    return 'My Contacts';
  } else if (path.includes(`contact-display`) && contactType === ContactType.ORG) {
    return 'Company Contacts';
  } else if (path.includes(`contact-display`) && contactType === ContactType.USER) {
    return 'My Contacts';
  }
  if (path.startsWith(`/${language}/platform`)) {
    return 'Platform Contacts';
  }
};

export const constructPersonLegalName = (legalName: ILegalName) => {
  let name = '';
  if (legalName?.type?.value === LegalNameType.MONONYM) {
    name = legalName?.name?.value;
  }
  if (legalName?.type?.value === LegalNameType.POLYNYM) {
    if (legalName?.firstName?.value) {
      name += ' ' + legalName.firstName?.value;
    }
    if (legalName?.middleName?.value) {
      name += ' ' + legalName.middleName?.value;
    }
    if (legalName?.maidenName?.value) {
      name += ' ' + legalName.maidenName?.value;
    }
    if (legalName?.lastName?.value) {
      name += ' ' + legalName.lastName?.value;
    }
  }
  return name?.trim();
};

export function getFullName(legalName: ILegalName, preferredName: ValueWithPronounce): string {
  if (preferredName?.value) return preferredName.value;
  return constructPersonLegalName(legalName);
}

export const constructPersonLegalPronunciation = (legalName: ILegalName) => {
  let pronunciation = '';
  if (legalName?.type?.value === LegalNameType.MONONYM) {
    pronunciation = legalName?.name?.pronunciation;
  }
  if (legalName?.type?.value === LegalNameType.POLYNYM) {
    if (legalName?.firstName?.pronunciation) {
      pronunciation += ' ' + legalName.firstName?.pronunciation;
    }
    if (legalName?.middleName?.pronunciation) {
      pronunciation += ' ' + legalName.middleName?.pronunciation;
    }
    if (legalName?.maidenName?.pronunciation) {
      pronunciation += ' ' + legalName.maidenName?.pronunciation;
    }
    if (legalName?.lastName?.pronunciation) {
      pronunciation += ' ' + legalName.lastName?.pronunciation;
    }
  }
  return pronunciation?.trim();
};

export function getFullPronunciation(legalName: ILegalName, preferredName: ValueWithPronounce): string {
  if (preferredName?.value) return preferredName?.pronunciation;
  return constructPersonLegalPronunciation(legalName);
}
export function getPublicProfileLink(profileLink: string) {
  return `${process.env.NEXT_PUBLIC_BASEPATH}/public-profile/${profileLink}`;
}

export function getShallowCopyOfArray<T>(array: T[]) {
  return array.map((item) => {
    return {...item};
  });
}

export function getLandingPagePreference(landingPreference: LandingPreference) {
  switch (landingPreference?.preferenceType) {
    case LandingPagePreferenceTypes.PersonalLandingPage:
      return 'user';

    case LandingPagePreferenceTypes.CompanyLandingPage:
      return 'company/' + landingPreference.extraOptions.companyGUID;

    case LandingPagePreferenceTypes.CompanyList:
      return 'user?showModel=quickCompanySwitch';
  }
  return 'user';
}

export function getLoggedInRedirection() {
  const redirectUrl = sessionStorage.getItem('redirectUrl');
  if (redirectUrl) {
    sessionStorage.removeItem('redirectUrl');
    return redirectUrl.replace(process.env.NEXT_PUBLIC_BASEPATH, '');
  }
}

export const getJdenticon = (profileLinkGUID: string, contactGUID: string) => {
  return profileLinkGUID || contactGUID;
};

export function isEqualObj(obj1, obj2, excludeFields = [], isParent = true) {
  if (typeof obj1 !== typeof obj2) {
    return false;
  }
  const obj1Keys = Object.keys(obj1 || {}).filter((x) => !excludeFields.includes(x));
  const obj2Keys = Object.keys(obj2 || {}).filter((x) => !excludeFields.includes(x));

  if (obj1Keys.length !== obj2Keys.length) {
    return false;
  }
  for (let i = 0; i < obj1Keys.length; i++) {
    if (!(obj1Keys[i] in obj2)) {
      return false;
    }
    const obj1Type = typeof obj1[obj1Keys[i]];
    const obj2Type = typeof obj2[obj1Keys[i]];
    if (obj1Type !== obj2Type) return false;

    if (obj1Type === 'object') {
      if (Array.isArray(obj1Keys[i])) {
        if (!Array.isArray(obj2Keys[i])) {
          return false;
        }
        for (let i = 0; i < obj1Keys[i].length; i++) {
          const isSame = isEqualObj(obj1[obj1Keys[i]], obj2[obj1Keys[i]], excludeFields, false);
          if (isSame === false) {
            return isSame;
          }
        }
      } else {
        const isSame = isEqualObj(obj1[obj1Keys[i]], obj2[obj1Keys[i]], excludeFields, false);
        if (isSame === false) {
          return false;
        }
      }
    } else {
      if (obj1[obj1Keys[i]] !== obj2[obj1Keys[i]]) {
        return false;
      }
    }
  }
  if (isParent) {
    return true;
  }
}
export const convertSharedFieldsToContactDetails = (sharedInData: SharedInContact) => {
  const {sharedFields, ...rest} = sharedInData;
  return {
    ...sharedFields,
    ...rest,
  };
};

export function bytesToSize(bytes: number) {
  const sizes = Object.values(FileSizeUnits);
  if (bytes === 0) return '0 Bytes';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
}

export function getCurrentContextFromPathName(pathName: string, locale: string | string[]) {
  if (locale === 'en' || locale === 'eng') {
    return pathName.startsWith('/platform') ? EntityType.PLATFORM : pathName.startsWith('/company') ? EntityType.ORG : EntityType.USER;
  } else {
    const pathNamePrefix = pathName.split(`/${locale}/`)[1];
    return pathNamePrefix.startsWith('platform') ? EntityType.PLATFORM : pathName.startsWith('company') ? EntityType.ORG : EntityType.USER;
  }
}

export function isUUID(uuid: string): uuid is UUID {
  if (typeof uuid !== 'string') {
    return false;
  }
  const segments = uuid.split('-');
  if (segments.length !== 5) {
    return false;
  }
  for (const segment of segments) {
    if (segment.length % 4 !== 0) {
      return false;
    }
    for (const char of segment) {
      if (!isHexCharacter(char)) {
        return false;
      }
    }
  }
  return true;
}

function isHexCharacter(char: string): boolean {
  const hexChars = '0123456789abcdefABCDEF';
  return hexChars.includes(char);
}

export function getPaginationSearchOptionsFromURLParams(searchParams: ReadonlyURLSearchParams) {
  return {
    paginationOptions: {
      page: Number(searchParams.get('page')) || DEFAULT_PAGE,
      pageSize: Number(searchParams.get('pageSize')) || DEFAULT_PAGE_SIZE_FOR_CARDS,
    },
    search: searchParams.get('search'),
  };
}

export function getPaginationSearchOptionsForInfiniteScrollFromURLParams(searchParams: ReadonlyURLSearchParams) {
  return {
    paginationOptions: {
      page: Number(searchParams.get('page')) || DEFAULT_PAGE,
      pageSize: Number(searchParams.get('pageSize')) || DEFAULT_PAGE_SIZE_FOR_INFINITE_SCROLL,
    },
    search: searchParams.get('search'),
  };
}

export function constructCustomEllipses(
  item: string,
  ellipsesCount: number = 3,
  ellipsesCharacter: string = '.',
  ellipsesStartIndexFromStart: number = 4,
  ellipsesEndIndexFromEnd: number = 5,
) {
  if (item.length > 0) {
    return `${item.substring(0, ellipsesStartIndexFromStart)}${ellipsesCharacter.repeat(ellipsesCount)}${item.substring(item.length - ellipsesEndIndexFromEnd)}`;
  }
}

export function deepClone(obj) {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  const clone = Array.isArray(obj) ? [] : {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      clone[key] = deepClone(obj[key]);
    }
  }

  return clone;
}

export const generateDefaultValuesforEditPolicy = (policy, effectivePolicy) => {
  return {
    passwordLength: policy?.password?.length,
    effectiveLength:
      effectivePolicy.upperCase?.length +
      effectivePolicy.lowerCase?.length +
      effectivePolicy.numerical?.length +
      effectivePolicy.specialChar?.length,
    upperCaseLength: policy.upperCase?.length,
    lowerCaseLength: policy.lowerCase?.length,
    numericalLength: policy.numerical?.length,
    specialCharLength: policy.specialChar?.length,
    passwordExpirationDays: policy.passwordExpirationDays?.value,
    webAppSessionTime: policy.webAppSessionTime?.value,
    mfa: policy.mfa?.value,
  };
};

export const getConnectionProfileUrl = (connectionId: string, contactType: ContactType, companyId: string = null) => {
  if (!companyId) {
    return `/user/my-account/my-connections/${connectionId}`;
  } else {
    return `/company/${companyId}/account-admin/${contactType === ContactType.USER ? 'user-connections' : 'b2b-connections'}/${connectionId}`;
  }
};

export function getTranslationFromModule(i18n: i18n, module: string, key: string, translate: TFunction) {
  return i18n.exists(key)
    ? translate(key)
    : i18n.exists(`common:${module}.${key}`)
      ? translate(`common:${module}.${key}`)
      : i18n.exists(`common:${key}`)
        ? translate(`common.${key}`)
        : translate(key);
}

export const getElementLeftRightPadding = (el: HTMLElement) => {
  if (!el) return 0;
  return Number(getComputedStyle(el).paddingLeft.replace('px', '')) + Number(getComputedStyle(el).paddingRight.replace('px', ''));
};

export const getGridRecordsTitle = (t: TFunction, record: any) => {
  const total = record?.total ?? 0;
  return {
    text: `${total === 1 ? `${total} ` + t('common.words.record') : `${total} ` + t('common.words.records')}`,
    className: 'text-sm font-normal',
  };
};

export const copyText = async (e: React.MouseEvent, text) => {
  e.stopPropagation();
  navigator.clipboard.writeText(text);
};
export const getUserOrgDeletionStatus = (userOrgData) =>
  userOrgData?.deleteInitiatedAt
    ? new Date(
        new Date(userOrgData?.deleteInitiatedAt).setDate(new Date(userOrgData?.deleteInitiatedAt).getDate() + PERMANENT_DELETION_DAYS),
      ) || userOrgData?.deletedAt
      ? RecordStatus.DELETED
      : RecordStatus.PENDING_DELETION
    : userOrgData?.deleteRecoveredAt
      ? RecordStatus.RECOVERED
      : undefined;

export const getUrlPrefix = (entityType: EntityType, entityGUID: string) => {
  return entityType === EntityType.ORG ? `/company/${entityGUID}/` : '/user/';
};
